import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';

const ProtectedRoute = ({ children }) => {
  const { isLoading, isAuthenticated, isAuthorized, login, logout, requiredGroup, user, configError } = useAuth();

  // Configuration error - show setup instructions
  if (configError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="bg-white/90 backdrop-blur-md p-8 rounded-2xl shadow-xl w-full max-w-2xl border border-gray-200">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Configuration Required
            </h1>
            <p className="text-red-600 font-medium mb-4">
              {configError}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              📋 Setup Instructions
            </h2>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
              <li>Follow the <strong>AWS Cognito + Azure AD SSO Implementation Guide</strong></li>
              <li>Create your Cognito User Pool in AWS Console</li>
              <li>Configure Azure AD Enterprise Application</li>
              <li>Update your <code className="bg-gray-200 px-1 rounded">.env</code> file with:</li>
            </ol>

            <div className="mt-4 bg-gray-800 text-green-400 p-4 rounded-lg font-mono text-xs overflow-x-auto">
              <div>REACT_APP_COGNITO_USER_POOL_ID=us-east-1_YourPoolId</div>
              <div>REACT_APP_COGNITO_CLIENT_ID=YourClientId</div>
              <div>REACT_APP_COGNITO_DOMAIN=your-domain.auth.us-east-1.amazoncognito.com</div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-blue-800 font-medium mb-2">🔍 Current Configuration:</h3>
            <div className="text-xs font-mono text-blue-700 space-y-1">
              <div>User Pool ID: <span className="text-red-600">{process.env.REACT_APP_COGNITO_USER_POOL_ID || 'Not set'}</span></div>
              <div>Client ID: <span className="text-red-600">{process.env.REACT_APP_COGNITO_CLIENT_ID || 'Not set'}</span></div>
              <div>Domain: <span className="text-red-600">{process.env.REACT_APP_COGNITO_DOMAIN || 'Not set'}</span></div>
            </div>
          </div>

          <p className="text-center text-gray-500 text-xs mt-6">
            After updating .env, restart the dev server
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-300">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF8F0] relative overflow-hidden font-['Inter',sans-serif]">
        {/* Artistic Background Elements with Framer Motion */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Vibrant Orange Orb */}
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.4, 0.6, 0.4],
              x: [0, 30, 0],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-[-15%] right-[-10%] w-[45rem] h-[45rem] bg-gradient-to-br from-orange-400/20 to-orange-200/20 rounded-full blur-[100px]"
          />
          {/* Teal/Blue Accent from Logo */}
          <motion.div
            animate={{
              scale: [1, 1.15, 1],
              rotate: [0, -20, 0],
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            className="absolute top-[30%] left-[-15%] w-[40rem] h-[40rem] bg-gradient-to-tr from-teal-200/20 to-emerald-100/20 rounded-full blur-[90px]"
          />
        </div>

        {/* Unique Glass Card with Entrance Animation */}
        <div className="relative z-10 mx-4">
          <div className="absolute -inset-0.5 bg-gradient-to-br from-orange-300 to-amber-300 rounded-[2.5rem] opacity-30 blur-sm"></div>

          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="relative bg-white/80 backdrop-blur-xl p-12 md:p-14 rounded-[2rem] shadow-[0_25px_50px_-12px_rgba(249,115,22,0.15)] w-full max-w-[440px] border border-white"
          >

            <div className="flex flex-col items-center mb-10">
              {/* Logo Entrance */}
              <motion.div
                initial={{ scale: 0, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
                className="relative mb-6"
              >
                <div className="absolute inset-0 bg-orange-400 blur-2xl opacity-20 rounded-full"></div>
                <div className="relative w-28 h-28 bg-gradient-to-b from-white to-orange-50 rounded-3xl flex items-center justify-center shadow-lg border border-orange-100/50">
                  <img
                    src="/CI-logo.png"
                    alt="Creyente Logo"
                    className="h-16 w-auto drop-shadow-md"
                  />
                </div>
              </motion.div>

              <div className="text-center space-y-1 mb-2">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-gray-500 font-medium text-sm px-4"
                >
                  Sign in to <span className="text-orange-600 font-bold">Creyente AI Agents</span>
                </motion.p>
              </div>
            </div>

            <div className="space-y-5">
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                whileHover={{ scale: 1.01, boxShadow: "0 10px 25px -5px rgba(249, 115, 22, 0.3)" }}
                whileTap={{ scale: 0.99 }}
                onClick={login}
                className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-[#0078d4] to-[#106ebe] hover:from-[#0086f0] hover:to-[#0078d4] text-white font-semibold py-4 px-6 rounded-xl transition-all duration-300 shadow-lg group"
              >
                {/* Microsoft Logo */}
                <svg className="w-5 h-5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
                <span>Sign in with SSO</span>
              </motion.button>
            </div>

            <div className="mt-10 pt-6 border-t border-dashed border-gray-200 text-center">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Powered by Creyente InfoTech</span>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-300">
        <div className="bg-white/80 backdrop-blur-md p-10 rounded-2xl shadow-xl w-full max-w-md border border-gray-200 text-center">
          <div className="text-red-500 text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-2">You don't have permission to access the Agent Portal.</p>
          <p className="text-gray-500 text-sm mb-4">Signed in as: {user?.email}</p>
          <p className="text-gray-500 text-sm mb-6">Required group: <span className="font-semibold text-orange-600">{requiredGroup}</span></p>
          <button
            onClick={logout}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-3 rounded-lg"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return children;
};

export default ProtectedRoute;